import { memo } from 'react';
import PropTypes from 'prop-types';
import { Navigate, Route, Routes } from 'react-router-dom';
import Loadable from 'react-loadable';

import NotFound from './NotFound';
import Logout from 'app/modules/authentication/section/authentication/page/Logout';
import { userIsDriver, userIsContractor } from '../util/hasAccess';
import formatDate from 'date-fns/format';

const ReportSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "report-section" */ './report/Routes'),
    loading: () => null,
    modules: ['report-section'],
    webpack: () => [require.resolveWeak('./report/Routes')], // Prevents white flash
});

const UserSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "user-section" */ './user/Routes'),
    loading: () => null,
    modules: ['user-section'],
    webpack: () => [require.resolveWeak('./user/Routes')], // Prevents white flash
});

const ClientSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "client-section" */ './client/Routes'),
    loading: () => null,
    modules: ['client-section'],
    webpack: () => [require.resolveWeak('./client/Routes')], // Prevents white flash
});

const ContractorSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "rds-section" */ './contractor/Routes'),
    loading: () => null,
    modules: ['contractor-section'],
    webpack: () => [require.resolveWeak('./contractor/Routes')], // Prevents white flash
});

//used for pages that a contractor should see
const ContractorPagesSection = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "rds-section" */ './contractorPages/Routes'
        ),
    loading: () => null,
    modules: ['contractor-pages-section'],
    webpack: () => [require.resolveWeak('./contractorPages/Routes')], // Prevents white flash
});

const DeliverySection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "delivery-section" */ './delivery/Routes'),
    loading: () => null,
    modules: ['delivery-section'],
    webpack: () => [require.resolveWeak('./delivery/Routes')], // Prevents white flash
});

const RunSection = Loadable({
    loader: () => import(/* webpackChunkName: "run-section" */ './run/Routes'),
    loading: () => null,
    modules: ['run-section'],
    webpack: () => [require.resolveWeak('./run/Routes')], // Prevents white flash
});

const WarehouseSection = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "warehouse-section" */ './warehouse/Routes'
        ),
    loading: () => null,
    modules: ['warehouse-section'],
    webpack: () => [require.resolveWeak('./warehouse/Routes')], // Prevents white flash
});

const PurchaseOrderSection = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "purchase-order-section" */ './purchaseOrder/Routes'
        ),
    loading: () => null,
    modules: ['purchase-order-section'],
    webpack: () => [require.resolveWeak('./purchaseOrder/Routes')], // Prevents white flash
});

const AdvancedShippingNotice = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "purchase-order-section" */ './advancedShippingNotice/Routes'
        ),
    loading: () => null,
    modules: ['advanced-shipping-notice-section'],
    webpack: () => [require.resolveWeak('./advancedShippingNotice/Routes')], // Prevents white flash
});

const ZoneSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "zone-section" */ './zone/Routes'),
    loading: () => null,
    modules: ['zone-section'],
    webpack: () => [require.resolveWeak('./zone/Routes')], // Prevents white flash
});

const BillingSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "zone-section" */ './billing/Routes'),
    loading: () => null,
    modules: ['billing-section'],
    webpack: () => [require.resolveWeak('./billing/Routes')], // Prevents white flash
});

const RdsSection = Loadable({
    loader: () => import(/* webpackChunkName: "rds-section" */ './rds/Routes'),
    loading: () => null,
    modules: ['rds-section'],
    webpack: () => [require.resolveWeak('./rds/Routes')], // Prevents white flash
});

const ProductSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "delivery-section" */ './product/Routes'),
    loading: () => null,
    modules: ['product-section'],
    webpack: () => [require.resolveWeak('./product/Routes')], // Prevents white flash
});

const WmsSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "rds-section" */ './dotwms/Routes'),
    loading: () => null,
    modules: ['dotwms-section'],
    webpack: () => [require.resolveWeak('./dotwms/Routes')], // Prevents white flash
});

const DevSection = Loadable({
    loader: () => import(/* webpackChunkName: "rds-section" */ './dev/Routes'),
    loading: () => null,
    modules: ['dev-section'],
    webpack: () => [require.resolveWeak('./dev/Routes')], // Prevents white flash
});

const TwilioFlexSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "rds-section" */ './twilioFlex/Routes'),
    loading: () => null,
    modules: ['twilioflex-section'],
    webpack: () => [require.resolveWeak('./twilioFlex/Routes')], // Prevents white flash
});

const SpecialSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "rds-section" */ './special/Routes'),
    loading: () => null,
    modules: ['special-section'],
    webpack: () => [require.resolveWeak('./special/Routes')], // Prevents white flash
});

const EquipmentSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "rds-section" */ './equipment/Routes'),
    loading: () => null,
    modules: ['equipment-section'],
    webpack: () => [require.resolveWeak('./equipment/Routes')], // Prevents white flash
});

const SmsSection = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "messaging-section" */ './messaging/Routes'
        ),
    loading: () => null,
    modules: ['messaging-section'],
    webpack: () => [require.resolveWeak('./messaging/Routes')], // Prevents white flash
});

const LeastCostRoutingSection = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "carrier-routing-section" */ './leastCostRouting/Routes'
        ),
    loading: () => null,
    modules: ['carrier-routing-section'],
    webpack: () => [require.resolveWeak('./leastCostRouting/Routes')], // Prevents white flash
});

const PalletSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "pallet-section" */ './pallet/Routes'),
    loading: () => null,
    modules: ['pallet-section'],
    webpack: () => [require.resolveWeak('./pallet/Routes')], // Prevents white flash
});

const LineHaulLegModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-leg-section" */ './lineHaulLeg/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-leg-section'],
    webpack: () => [require.resolveWeak('./lineHaulLeg/Routes')], // Prevents white flash
});

const LineHaulLegScheduleModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-leg-schedule-section" */ './lineHaulLegSchedule/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-leg-schedule-section'],
    webpack: () => [require.resolveWeak('./lineHaulLegSchedule/Routes')], // Prevents white flash
});

const LineHaulTrailerModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-trailer-section" */ './lineHaulTrailer/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-trailer-section'],
    webpack: () => [require.resolveWeak('./lineHaulTrailer/Routes')], // Prevents white flash
});

const LineHaulTruckModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-truck-section" */ './lineHaulTruck/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-truck-section'],
    webpack: () => [require.resolveWeak('./lineHaulTruck/Routes')], // Prevents white flash
});

const LineHaulMovementModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-movement-section" */ './lineHaulMovement/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-movement-section'],
    webpack: () => [require.resolveWeak('./lineHaulMovement/Routes')], // Prevents white flash
});

const LineHaulJobModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-job-section" */ './lineHaulJob/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-job-section'],
    webpack: () => [require.resolveWeak('./lineHaulJob/Routes')], // Prevents white flash
});

const LineHaulSubcontractorModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-subcontractor-section" */ './lineHaulSubcontractor/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-subcontractor-section'],
    webpack: () => [require.resolveWeak('./lineHaulSubcontractor/Routes')], // Prevents white flash
});

const LineHaulDollyModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-dolly-section" */ './lineHaulDolly/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-dolly-section'],
    webpack: () => [require.resolveWeak('./lineHaulDolly/Routes')], // Prevents white flash
});

const LineHaulJourneyModule = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "line-haul-journey-section" */ './lineHaulJourney/Routes'
        ),
    loading: () => null,
    modules: ['line-haul-journey-section'],
    webpack: () => [require.resolveWeak('./lineHaulJourney/Routes')], // Prevents white flash
});

const AppSection = Loadable({
    loader: () =>
        import(/* webpackChunkName: "delivery-section" */ './app/Routes'),
    loading: () => null,
    modules: ['app-section'],
    webpack: () => [require.resolveWeak('./app/Routes')], // Prevents white flash
});
const ClientToolsSection = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "client-tools-section" */ './clientTools/Routes'
        ),
    loading: () => null,
    modules: ['client-tools-section'],
    webpack: () => [require.resolveWeak('./clientTools/Routes')], // Prevents white flash
});

const ModuleRoutes = memo(({ auth, navigate }) => {
    if (userIsDriver(auth.user.role)) {
        return (
            <Routes>
                <Route exact path="/" element={<ReportSection auth={auth} />} />
                <Route
                    key="logout"
                    path="/auth/logout"
                    element={<Logout navigate={navigate} />}
                />
                <Route path="*" element={<NotFound />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route
                exact
                path="/"
                element={
                    <Navigate
                        to={
                            userIsContractor(auth.user.role)
                                ? `/run/r/country/1/${formatDate(new Date(), 'yyyy-MM-dd')}`
                                : '/d/overview'
                        }
                    />
                }
            />
            <Route path="/r/*" element={<ReportSection />} />
            <Route path="/u/*" element={<UserSection />} />
            <Route path="/d/*" element={<DeliverySection />} />
            <Route path="/run/*" element={<RunSection auth={auth} />} />
            <Route path="/c/*" element={<ClientSection />} />
            <Route path="/ctr/*" element={<ContractorSection />} />
            <Route path="/ct/*" element={<ContractorPagesSection />} />
            <Route path="/po/*" element={<PurchaseOrderSection />} />
            <Route path="/asn/*" element={<AdvancedShippingNotice />} />
            <Route path="/z/*" element={<ZoneSection />} />
            <Route path="/b/*" element={<BillingSection />} />
            <Route path="/wh/*" element={<WarehouseSection />} />
            <Route path="/rds/*" element={<RdsSection />} />
            <Route path="/wms/*" element={<WmsSection />} />
            <Route path="/p/*" element={<ProductSection />} />
            <Route path="/dev/*" element={<DevSection />} />
            <Route path="/twilio-flex/*" element={<TwilioFlexSection />} />
            <Route path="/special/*" element={<SpecialSection />} />
            <Route path="/mhe/*" element={<EquipmentSection />} />
            <Route path="/messaging/*" element={<SmsSection />} />
            <Route
                path="/carrier-routing/*"
                element={<LeastCostRoutingSection />}
            />
            <Route path="/pallet/*" element={<PalletSection />} />
            <Route path="/app/*" element={<AppSection />} />
            <Route path="/line-haul-leg/*" element={<LineHaulLegModule />} />
            <Route
                path="/line-haul-leg-schedule/*"
                element={<LineHaulLegScheduleModule />}
            />
            <Route
                path="/line-haul-trailer/*"
                element={<LineHaulTrailerModule />}
            />
            <Route
                path="/line-haul-dolly/*"
                element={<LineHaulDollyModule />}
            />
            <Route
                path="/line-haul-truck/*"
                element={<LineHaulTruckModule />}
            />
            <Route
                path="/line-haul-journey/*"
                element={<LineHaulJourneyModule />}
            />
            <Route
                path="/line-haul-movement/*"
                element={<LineHaulMovementModule />}
            />
            <Route path="/line-haul-job/*" element={<LineHaulJobModule />} />
            <Route
                path="/line-haul-subcontractor/*"
                element={<LineHaulSubcontractorModule />}
            />
            <Route
                exact
                path="/sms/block/all"
                element={<Navigate to="/messaging/sms-block/all" />}
            />
            <Route path="/client-tools/*" element={<ClientToolsSection />} />
            <Route
                key="logout"
                path="/auth/logout"
                element={<Logout navigate={navigate} />}
            />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
});
ModuleRoutes.displayName = 'ModuleRoutes';
ModuleRoutes.propTypes = {
    auth: PropTypes.shape({
        user: PropTypes.shape({ role: PropTypes.string }),
    }).isRequired,
    navigate: PropTypes.func,
};

export default ModuleRoutes;
